import React, { useEffect, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  AreaChart,
  Area,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DateRange } from "../../../components/DateRange";
import { machineProfile, sensor } from "../../../api";
import { subMonths, startOfDay, endOfDay } from "date-fns";
import ConfirmationModal from "../../../components/ConfirmationDlg";
import {
  formatDateOnly,
  formatDateTime,
  formatTimeOnly,
} from "../../../utils/helper";
function TemperatureChart({ sensorData, machineId }) {
  const [temperatureData, setTemperatureData] = useState([]);
  const [maxY, setMaxY] = useState(120);
  const [threshold, setThreshold] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [thresholdData, setThresholdData] = useState(null);
  const [overview, setOverview] = useState({
    minimum: 0,
    average: 0,
    maximum: 0,
  });
  const [selectedInterval, setSelectedInterval] = useState("Month");
  const [date, setDate] = useState({
    startDate: startOfDay(subMonths(new Date(), 12)),
    endDate: endOfDay(new Date()),
  });
  const getThershold = async () => {
    const threshold = await machineProfile.getTempThreshold(machineId, {
      deployment_id: sensorData.deployments_id,
      feature_name: "temperature",
    });
    if (threshold.ok) {
      const body = await threshold.json();
      let data = body.data;
      if (data.length) {
        const th = data[0][3];
        setThreshold(th);
        setThresholdData(th);
      }
    }
  };
  useEffect(() => {
    const getData = async () => {
      const query = {
        sensor_id: sensorData.internal_id,
        machine_element_id: sensorData.machine_element_id,
        deployment_id: sensorData.deployments_id,
        interval: selectedInterval,
        start: date?.startDate,
        end: date?.endDate,
      };
      const result = await sensor.liveTemperature(query);
      if (result.ok) {
        const body = await result.json();
        let data = body.data;

        if (selectedInterval == "Month")
          data = data.map((s) => ({
            ...s,
            name: formatDateOnly(s.name),
            name_full: formatDateTime(s.name),
          }));
        if (selectedInterval == "Week")
          data = data.map((s) => ({
            ...s,
            name: formatDateOnly(s.name),
            name_full: formatDateTime(s.name),
          }));
        if (selectedInterval == "Day")
          data = data.map((s) => ({
            ...s,
            name: formatDateTime(s.name),
            name_full: formatDateTime(s.name),
          }));
        if (selectedInterval == "Hour")
          data = data.map((s) => ({
            ...s,
            name: formatTimeOnly(s.name),
            name_full: formatDateTime(s.name),
          }));

        if (selectedInterval == "Custom")
          data = data.map((s) => ({
            ...s,
            name: formatDateTime(s.name),
            name_full: formatDateTime(s.name),
          }));

        const min = Math.min(
          ...data.map((d) => {
            if (d.temperature) return d.temperature;
          })
        );
        const average = Math.ceil(
          data.map((d) => d.temperature).reduce((a, b) => a + b, 0) /
            data.length
        );
        const max = Math.max(...data.map((d) => d.temperature));

        setOverview({
          minimum: min,
          average: average,
          maximum: max,
        });

        setTemperatureData(data.reverse());
      }
    };
    getThershold();

    getData();

    const syncData = setInterval(() => {
      getData();
    }, 45000);

    return () => {
      clearInterval(syncData);
    };
  }, [selectedInterval]);
  const handleChangeDate = (startDate, endDate) => {
    setDate({
      startDate,
      endDate,
    });
    setSelectedInterval("Custom");
  };
  const CustomTooltip = ({ active, payload, label }) => {
    console.log({ active, payload, label });
    if (active && payload && payload.length) {
      return (
        <div style={{ background: "rgba(0,0,0,0.8)", padding: "5px" }}>
          {`Date : ${payload[0]?.payload?.name_full}`} <br />
          {`Temp : ${parseFloat(payload[0]?.value).toFixed(2)}`} <br />
        </div>
      );
    }

    return null;
  };
  const onClose = () => {
    setOpenModal(false);
    setThreshold(thresholdData);
  };
  const onSubmit = async () => {
    const result = await machineProfile.addTemperatureThreshold(machineId, {
      deployment_id: sensorData.deployments_id,
      feature_name: "temperature",
      threshold: threshold,
    });
    if (result.ok) {
      setOpenModal(false);
      setThresholdData(threshold);
      getThershold();
    }
  };
  return (
    <>
      <ConfirmationModal
        open={openModal}
        setOpen={setOpenModal}
        title="Set Threshold"
        btnSubmitText={"Save"}
        btnCancelText={"Cancel"}
        onClose={onClose}
        onSubmit={onSubmit}
      >
        <div className="row">
          <div className="col-md-12">
            <div className="form-group">
              <label>Set Threshold</label>
              <input
                type="number"
                className="form-control"
                value={threshold}
                onChange={(e) => {
                  setThreshold(e.target.value);
                }}
                placeholder="Set Threshold"
              />
            </div>
          </div>
        </div>
      </ConfirmationModal>
      <p style={{ textAlign: "left", marginLeft: "50px" }}></p>
      <div className="row justify-content-between mb-4">
        <div className="col-md-4 col-sm-6">
          {["Month", "Week", "Day"].map((interval) => (
            <button
              onClick={() => setSelectedInterval(interval)}
              className={`btn btn-sm ${
                interval === selectedInterval
                  ? "btn-theme"
                  : "btn-outline-theme"
              }`}
              key={interval}
            >
              {interval}
            </button>
          ))}
        </div>
        <div class="col-md-4">
          <div className="d-flex justify-content-end">
            <button
              onClick={() => setOpenModal(true)}
              className="btn btn-sm btn-outline-theme"
            >
              Set Threshold
            </button>
          </div>
        </div>

        <div className="col-md-4  col-sm-6">
          <div>
            <DateRange
              onChange={handleChangeDate}
              startDate={date.startDate}
              endDate={date.endDate}
            />
          </div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart
          width={500}
          height={300}
          fontSize={13}
          margin={{ left: 0 }}
          data={temperatureData}
        >
          <CartesianGrid strokeDasharray="3 3" />
          {thresholdData && (
            <ReferenceLine
              y={thresholdData}
              stroke="red"
              strokeDasharray="3 3"
              label={{
                value: `Threshold ${thresholdData}°C`,
                fill: "#ffa6a6",
                position: "top",
              }}
            />
          )}
          <XAxis
            dataKey={"name"}
            tick={{ fill: "white" }}
            tickLine={false}
            height={60}
            label={{
              value: "Date",

              position: "center",
            }}
          />
          <YAxis
            width={90}
            domain={[0, maxY]}
            tick={{ fill: "white" }}
            label={{
              value: "Temperature °C",
              angle: -90,
              position: "center",
            }}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            connectNulls={true}
            type={"monotone"}
            dot={false}
            dataKey={"temperature"}
            stroke="#ffcd6a"
            fill="#ffcd6a"
            strokeWidth={1}
          />

          {/* TODO: This needs to be refactored */}
        </AreaChart>
      </ResponsiveContainer>
    </>
  );
}
export default TemperatureChart;
